/**
 * Craps state map factory.
 *
 * Returns the full set of craps state handlers. Populated per-state
 * during Phase 3 of the v2 migration; until each state class lands
 * here, the entry uses a no-op `State` placeholder so the state machine
 * can register without crashing on unhandled phases.
 *
 * Active flow (post-collapse):
 *   WAITING → STARTING → HI_LO → SHOOTER_SET_STACK → FADING →
 *   SIDEBET_BETTING → COME_OUT → POINT_ESTABLISHED → POINT_ROLL →
 *     (continue) POINT_RESULT → POINT_ROLL
 *     (terminal) SHOOTER_SET_STACK
 *
 * COME_OUT_RESULT, SHOOTER_WINS, SHOOTER_LOSES, ROUND_END are legacy
 * enum values not entered in the active flow but still registered
 * (as no-ops) for safety on stray transitions.
 */

import { CrapsState } from '@sdl/shared';
import { State } from '../State.js';
import type { StateMachine } from '../StateMachine.js';
import type { SDLGame } from '../SDLGame.js';
import { WaitingState } from '../states/WaitingState.js';
import { StartingState } from '../states/StartingState.js';
import { RoundEndState } from '../states/RoundEndState.js';
import { ShooterSetStackState } from '../states/ShooterSetStackState.js';
import { CrapsFadingState } from '../states/CrapsFadingState.js';
import { SidebetBettingState } from '../states/SidebetBettingState.js';
import { ComeOutState } from '../states/ComeOutState.js';
import { PointRollState } from '../states/PointRollState.js';
import { CrapsHiLoState } from '../states/CrapsHiLoState.js';
import { PointEstablishedState } from '../states/PointEstablishedState.js';
import { PointResultState } from '../states/PointResultState.js';
import { GameEndState } from '../states/GameEndState.js';

export function buildCrapsStates(
  sm: StateMachine,
  game: SDLGame,
): Map<string, State> {
  const states = new Map<string, State>();

  const noop = new State(sm, game);
  states.set(CrapsState.WAITING, new WaitingState(sm, game));
  states.set(CrapsState.STARTING, new StartingState(sm, game));
  states.set(CrapsState.HI_LO, new CrapsHiLoState(sm, game));
  states.set(CrapsState.SHOOTER_SET_STACK, new ShooterSetStackState(sm, game));
  states.set(CrapsState.FADING, new CrapsFadingState(sm, game));
  states.set(CrapsState.SIDEBET_BETTING, new SidebetBettingState(sm, game));
  states.set(CrapsState.COME_OUT, new ComeOutState(sm, game));
  states.set(CrapsState.COME_OUT_RESULT, noop);
  states.set(CrapsState.POINT_ESTABLISHED, new PointEstablishedState(sm, game));
  states.set(CrapsState.POINT_ROLL, new PointRollState(sm, game));
  states.set(CrapsState.POINT_RESULT, new PointResultState(sm, game));
  states.set(CrapsState.SHOOTER_WINS, noop);
  states.set(CrapsState.SHOOTER_LOSES, noop);
  states.set(CrapsState.ROUND_END, new RoundEndState(sm, game));
  states.set(CrapsState.GAME_END, new GameEndState(sm, game));

  return states;
}
